import React from "react";
import { ArrowRight, Shield, Heart, Compass } from "lucide-react";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_diaspora-care-hub/artifacts/gkc73wzr_WhatsApp%20Image%202026-06-29%20at%202.40.27%20PM.jpeg";

const HeroCtas = () => (
  <div className="mt-10 flex flex-wrap gap-4">
    <a
      href="#contact"
      data-testid="hero-primary-cta"
      className="group inline-flex items-center gap-3 bg-[var(--gg-primary)] text-white hover:bg-[var(--gg-primary-dark)] transition-all rounded-full pl-7 pr-3 py-3 text-[15px] shadow-lg shadow-[var(--gg-primary)]/25"
    >
      Book Consultation
      <span className="w-9 h-9 rounded-full bg-white/15 flex items-center justify-center group-hover:translate-x-1 transition-transform">
        <ArrowRight size={16} />
      </span>
    </a>
    <a
      href="/senior-care"
      data-testid="hero-secondary-cta"
      className="inline-flex items-center gap-2 text-[var(--gg-deep)] border border-[var(--gg-primary)]/40 hover:border-[var(--gg-primary)] hover:bg-white transition-all rounded-full px-7 py-3 text-[15px] font-medium"
    >
      Explore our services
    </a>
  </div>
);

const TrustStrip = () => (
  <div className="mt-14 grid grid-cols-3 gap-6 max-w-lg">
    <Trust icon={<Shield size={18} />} label="HEART Trust NTA Certified" />
    <Trust icon={<Heart size={18} />} label="85% Client Retention" />
    <Trust icon={<Compass size={18} />} label="Diaspora-Friendly Care" />
  </div>
);

const HeroImageCard = () => (
  <div className="relative aspect-[4/5] sm:aspect-[4/5] lg:aspect-[5/6] rounded-3xl overflow-hidden shadow-2xl shadow-[var(--gg-primary)]/20 animate-fade-up">
    <img
      src={HERO_IMG}
      alt="Two senior women enjoying a joyful moment in a tropical Jamaican garden"
      className="absolute inset-0 w-full h-full object-cover"
    />
    <div
      className="absolute inset-0 pointer-events-none"
      style={{
        background:
          "linear-gradient(180deg, transparent 60%, rgba(61,46,90,0.25) 100%)",
      }}
    />
    <div className="absolute bottom-6 left-6 right-6 lg:left-8 lg:right-8">
      <div className="bg-white/95 backdrop-blur rounded-2xl px-5 py-4 shadow-lg flex items-center gap-4">
        <span className="w-11 h-11 rounded-full bg-[var(--gg-primary)]/15 flex items-center justify-center text-[var(--gg-primary)]">
          <Heart size={18} />
        </span>
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-[var(--gg-primary)] font-semibold">
            Care with compassion
          </p>
          <p className="text-sm text-[var(--gg-deep)] mt-0.5 font-medium">
            Certified caregivers · vetted &amp; trusted
          </p>
        </div>
      </div>
    </div>
  </div>
);

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-24 pb-16 overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #FFE5D9 0%, #F5BFA1 35%, #E8DCF3 100%)",
      }}
    >
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 min-h-[calc(100vh-6rem)] flex flex-col justify-center">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1 animate-fade-up">
            <div className="flex items-center gap-3 mb-7">
              <span className="h-px w-10 bg-[var(--gg-primary)]" />
              <span className="eyebrow">Grace is sufficient.</span>
            </div>

            <h1
              data-testid="hero-headline"
              className="font-serif text-5xl sm:text-6xl lg:text-7xl text-[var(--gg-deep)] leading-[1.02] tracking-tight text-balance"
            >
              Compassionate care.
              <br />
              <em className="not-italic text-[var(--gg-primary)] font-normal">
                Confident living.
              </em>
              <br />A seamless return home.
            </h1>

            <p className="mt-8 text-lg lg:text-xl text-[var(--gg-text)] leading-relaxed max-w-xl">
              Graceful Guidance Ltd. is Jamaica&apos;s trusted senior support and
              relocation enterprise — helping older adults live independently with
              dignity, and guiding the diaspora home with a steady hand.
            </p>

            <HeroCtas />
            <TrustStrip />
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2">
            <HeroImageCard />
          </div>
        </div>
      </div>
    </section>
  );
}

function Trust({ icon, label }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="mt-0.5 text-[var(--gg-primary)]">{icon}</span>
      <span className="text-xs uppercase tracking-[0.15em] text-[var(--gg-text-muted)] leading-snug font-medium">
        {label}
      </span>
    </div>
  );
}
